import type { Receipt } from './receipt.js'

/**
 * O que a casa ainda precisa fazer com as notas.
 *
 * ## Por que "pendente" não é uma coisa só
 *
 * `status` conta duas histórias diferentes. `uploaded` e `processing` são
 * espera **do app** — a IA está lendo, e não há nada a fazer além de aguardar.
 * `extracted` e `failed` são espera **da pessoa**: a primeira pede revisão
 * (nada financeiro foi aplicado até alguém confirmar — é o princípio 5 dentro
 * de um campo de status), e a segunda pede uma decisão sobre o que deu errado.
 *
 * Somar as quatro num número só diria "4 pendentes" para uma casa que não tem
 * nada a fazer, e é o oposto do que um resumo serve.
 *
 * ## E "sem transação" é outra coisa ainda
 *
 * `transactionId: null` é **aceitável** — o próprio tipo diz isso —, e uma nota
 * confirmada sem transação continua valendo: ela gerou histórico de preço, que
 * é o que este app acumula. O que ela não faz é detalhar um lançamento. Por
 * isso ela conta separado, e não entra em "a revisar": não é erro, é um
 * vínculo que ainda não foi feito.
 */
export type ResumoDeNotas = {
  /** Esperando a pessoa: revisar o que a IA leu, ou resolver o que falhou. */
  aRevisar: number
  /** Esperando o app: na fila ou sendo lida. */
  emLeitura: number
  /** Confirmadas ou não, sem lançamento a que se ligar. */
  semTransacao: number
  total: number
}

export function resumirNotas(receipts: Receipt[]): ResumoDeNotas {
  return {
    aRevisar: receipts.filter((r) => r.status === 'extracted' || r.status === 'failed').length,
    emLeitura: receipts.filter((r) => r.status === 'uploaded' || r.status === 'processing').length,
    semTransacao: receipts.filter((r) => r.transactionId === null).length,
    total: receipts.length,
  }
}

/**
 * A frase do cartão da home — **uma linha, a mais urgente primeiro**.
 *
 * A ordem é a de quem pode agir: o que espera a pessoa vem antes do que espera
 * a máquina, e o vínculo que falta vem por último porque não é erro nenhum.
 * Sem nada disso, o cartão diz o tamanho do acervo em vez de inventar uma
 * pendência.
 */
export function frasePendencias(resumo: ResumoDeNotas): string {
  if (resumo.total === 0) return 'Fotografe o cupom'
  if (resumo.aRevisar > 0) {
    return resumo.aRevisar === 1 ? '1 a revisar' : `${String(resumo.aRevisar)} a revisar`
  }
  if (resumo.emLeitura > 0) {
    return resumo.emLeitura === 1 ? '1 sendo lida' : `${String(resumo.emLeitura)} sendo lidas`
  }
  if (resumo.semTransacao > 0) {
    return resumo.semTransacao === 1
      ? '1 sem lançamento'
      : `${String(resumo.semTransacao)} sem lançamento`
  }
  return resumo.total === 1 ? '1 nota, tudo em dia' : `${String(resumo.total)} notas, tudo em dia`
}
